import React, { useState } from 'react'
import { Link } from 'gatsby'
import { useAuthService } from 'theme/services/auth/auth-service'
import PropTypes from 'prop-types'
import { MODES } from 'src/components/modals/login-modal'

const ForgotPasswordForm = ({ changeMode }) => {
  const authService = useAuthService()

  const [manualError, setManualError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')
  const [isLoggingIn, setLoggingIn] = useState(false)

  const handleForm = async (e) => {
    e.preventDefault()
    setManualError('')
    setSuccessMessage('')
    try {
      setLoggingIn(true)
      const email = document.getElementById('email').value
      await authService.forgotPassword(email)
      setSuccessMessage('We just sent a recovery link. You can auth by new password')
      setLoggingIn(false)
    } catch (e) {
      setLoggingIn(false)
      if (e.code === 'auth/user-not-found') {
        setManualError('That email address is not registered. Please register your account.')
      } else {
        setManualError(e.message)
      }
    }
  }

  return (
    <div className="mx-auto w-full">
      <div>
        <h2 className="mt-6 text-3xl leading-9 font-extrabold text-gray-900">Forgot your Password</h2>
      </div>

      <div className="mt-8">
        <div className="mt-6">
          <form onSubmit={handleForm} method="POST" className="space-y-6">
            <div>
              <label htmlFor="email" className="block text-sm font-medium leading-5 text-gray-700">
                Email address
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="email"
                  type="email"
                  required
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm leading-5">
                {changeMode && (
                  <button
                    type="button"
                    onClick={() => changeMode(MODES.LOGIN)}
                    className="font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline transition ease-in-out duration-150"
                  >
                    Sign In
                  </button>
                )}
                {!changeMode && (
                  <Link
                    to="/login"
                    className="font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline transition ease-in-out duration-150"
                  >
                    Sign In
                  </Link>
                )}
              </div>

              <div className="text-sm leading-5">
                {changeMode && (
                  <button
                    type="button"
                    onClick={() => changeMode(MODES.REGISTER)}
                    className="font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline transition ease-in-out duration-150"
                  >
                    Register Now
                  </button>
                )}
                {!changeMode && (
                  <Link
                    to="/register"
                    className="font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:underline transition ease-in-out duration-150"
                  >
                    Register Now
                  </Link>
                )}
              </div>
            </div>

            {manualError && (
              <div className="rounded-md bg-red-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    {/* <!-- Heroicon name: x-circle --> */}
                    <svg
                      className="h-5 w-5 text-red-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm leading-5 font-medium text-red-800">{manualError}</h3>
                  </div>
                </div>
              </div>
            )}

            {successMessage && (
              <div className="rounded-md bg-green-50 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    {/* <!-- Heroicon name: check-circle --> */}
                    <svg
                      className="h-5 w-5 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm leading-5 font-medium text-green-800">{successMessage}</h3>
                  </div>
                </div>
              </div>
            )}

            <div>
              <span className="block w-full rounded-md shadow-sm">
                <button
                  type="submit"
                  disabled={isLoggingIn || successMessage}
                  className={`${
                    isLoggingIn || successMessage ? 'bg-indigo-400' : 'bg-indigo-600 hover:bg-indigo-500'
                  } w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white  focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition duration-150 ease-in-out`}
                >
                  {isLoggingIn ? 'Requesting' : 'Request Password Reset'}
                </button>
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

ForgotPasswordForm.propTypes = {
  changeMode: PropTypes.func,
}

export default ForgotPasswordForm
